import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
// services
import { MessageService } from '../../../../services/message.service'
import { MasterService } from '../../../../services/master.service'
import { StoreService } from '../../../../services/store.service'
import * as languageLibrary from '../../../../services/language'

import { INVENTORY } from '../../../../constants/inventory'
import { Subject } from 'rxjs';
import { Subscription } from 'rxjs';

import { formatCurrency, formatDecimal } from 'src/app/utils/format';
import { clean } from 'src/app/utils/numeric';
import { minValueValidator } from 'src/app/utils/min-value.validator';
import { OdometerType } from 'src/app/models/odometer-type.model';
import { Color } from 'src/app/models/color.model';
import { VehicleType } from 'src/app/models/vehicle-type.model';

@Component({
    selector: 'inventory-quick-add',
    templateUrl: './inventory-quick-add.component.html',
    styleUrls: ['./inventory-quick-add.component.scss'],
    standalone: false
})
export class InventoryQuickAddComponent implements OnInit, OnDestroy {

    /*
    * Variables
    */

    // save the language
    @Input() language: string = 'EN'

    @Input('saved') saved: Subject<any>

    @Output() validity = new EventEmitter<boolean>()

    @Output() created = new EventEmitter<any>()

    // set all words
    protected words = languageLibrary.language

    //
    private features: Object | null = null
    
    // list of odometers
    protected odometers: OdometerType[] = INVENTORY.ODOMETER_TYPES
    
    // list of colors
    protected colors: Color[] = INVENTORY.COLORS
    
    // list of Vehicle Types
    protected vehicleTypes: VehicleType[] = INVENTORY.VEHICLE_TYPES

    // Indicates a duplicate VIN was found.
    protected dupe: boolean = false
    
    // Indicates the vehicle is currently being saved.
    private saving: boolean = false

    protected markAsDeleted: boolean = false

    protected information = new FormGroup({
        id: new FormControl<number | null>(null),
        vehicleId: new FormControl<number | null>(null),
        lotId: new FormControl<number | null>(null),
        statusId: new FormControl<number>(3),
        dateOnLot: new FormControl<string>(new Date().toISOString().substring(0, 10)),
        vinNumber: new FormControl<string | null>(null, [Validators.required]),
        stockNumber: new FormControl<string | null>(null, [Validators.required]),
        cleanStockNumber: new FormControl<string | null>(null, []),        
        sellingPrice: new FormControl<string>(formatCurrency(0), minValueValidator(0)),
        modelYear: new FormControl<string | null>(null),
        make: new FormControl<string | null>(null),
        model: new FormControl<string | null>(null),
        miles: new FormControl<string | null>(null, [Validators.pattern('[0-9,]{1,20}'), minValueValidator(0)]),
        odometer: new FormControl<string>('ACTUAL', [Validators.required]),
        exteriorColor: new FormControl<string | null>(null),
        interiorColor: new FormControl<string | null>(null),
        type: new FormControl<string>('Car'),
        cylinders: new FormControl<string | null>(null),
        transmission: new FormControl<string | null>(null),
        driveline: new FormControl<string | null>(null),
        bodyStyle: new FormControl<string | null>(null),
        trimLevel: new FormControl<string | null>(null),
        engine: new FormControl<string | null>(null),
        curbWeight: new FormControl<string | null>(null),
        carryingWeight: new FormControl<string | null>(null),
        gvwr: new FormControl<string | null>(null),
        tonnage: new FormControl<string | null>(null),
        certifyLevel: new FormControl<string | null>(null),
    })

    get vinNumber() { return this.information.get('vinNumber') }
    get stockNumber() { return this.information.get('stockNumber') }
    get certifyLevel() { return this.information.get('certifyLevel') }

    private subscriptions: Subscription[] = []

    /*
    * life cycles
    */
    constructor(private router: Router, private route: ActivatedRoute, private ms: MessageService, private master: MasterService, private store: StoreService) {}

    ngOnInit() {
        const lotId = parseInt(localStorage.getItem('lot') || this.store.lotSelected)
        this.information.get("lotId").setValue(lotId)

        this.validity.next(this.information.valid)
        this.subscriptions.push(
            this.information.valueChanges.subscribe(() => {
                this.validity.next(this.information.valid)
            })
        )

        this.subscriptions.push(
            this.saved.subscribe((e) => {
                if (this.information.invalid) {
                    return
                }
                
                this.createInventory()
            })
        )
    }

    ngOnChanges(changes: SimpleChanges): void {
        //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
        //Add '${implements OnChanges}' to the class.
    }

    /*
    * functions
    */

    public saveInventory() {
        this.createInventory()
    }

    public createInventory() {
        if (this.information.invalid) {
            return
        }

        const payload = JSON.parse(JSON.stringify(this.information.value))
        payload['sellingPrice'] = clean(payload['sellingPrice'])
        payload['miles'] = clean(payload['miles'])

        this.master.post(`inventory`, { inventoryData: payload, featuresList: JSON.stringify(this.features) }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.data.error == 'Error: Stock Number is taken.') {
                this.information.controls['stockNumber'].setErrors({ 'duplicateFound': "Error! Stock Number is taken." });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.created.emit(res.data.inventory)
        })
    }

    public decodeVIN(stockNumber: string, vinNumber: string) {
        if (!vinNumber) {
            return
        }

        // set stock Number and check duplicate VIN
        this.setStockNumber(stockNumber, vinNumber)

        //decode VIN here
        this.decode(vinNumber)
    }

    public decode(vinNumber: string) {
        const lotId = this.information.get('lotId').value
        if (!lotId || isNaN(lotId) || (lotId <= 0)) {
            return
        }

        this.master.get(`vendors/vin/decode?vinNumber=${vinNumber}&lotId=${lotId}`, res => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            const decoded = res.data
            this.information.get("modelYear").setValue(decoded.modelYear)
            this.information.get("make").setValue(decoded.make)
            this.information.get("model").setValue(decoded.model)
            this.information.get("cylinders").setValue(decoded.cylinders || null)
            this.information.get("transmission").setValue(decoded.transmission || null)
            this.information.get("driveline").setValue(decoded.driveline || null)
            this.information.get("trimLevel").setValue(decoded.trimLevel || null)
            this.information.get("bodyStyle").setValue(decoded.bodyStyle || null)
            this.information.get("engine").setValue(decoded.engine || null)
            this.information.get("curbWeight").setValue(decoded.curbWeight || null)
            this.features = decoded.features
        })
    }

    public setStockNumber(stockNumber: string, vinNumber: string): any {
        if (stockNumber !== "" && !this.information.controls['stockNumber'].hasError) {
            return
        }

        const lotId = this.information.get('lotId').value
        if (!lotId || isNaN(lotId) || (lotId <= 0)) {
            return
        }

        this.master.get(`inventory/stock/next?lotId=${lotId}&vinNumber=${vinNumber}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            const info = res.data.inventoryStockData
            if (!info) {
                return
            }

            const stockNumber = info.preStockNumber || ''
            this.information.get("stockNumber").setValue(stockNumber)
            this.information.get("cleanStockNumber").setValue(stockNumber)
            this.information.get("certifyLevel").setValue(info['certifyLevel'])

            this.checkDuplicateVIN(stockNumber, vinNumber)
        })
    }

    public checkDuplicateVIN(stockNumber: string, vinNumber: string) {
        if (!vinNumber || (vinNumber.length <= 0)) {
            return
        }

        this.dupe = false
        this.master.get(`inventory/vin/exists?vinNumber=${vinNumber}&stockNumber=${stockNumber}`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status === 200) {
                return
            }

            this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
            this.dupe = (res.status === 409)
        })
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }
}


